import type { LoadMotion, LoadStatus, LoadStopStatus } from '@breaker/shared-types';

/**
 * Status grouping for UI (S79 three-layer split — paperwork only).
 *   - neutral: draft (not committed yet)
 *   - info:    confirmed, assigned (committed; motion is the stops' business)
 *   - success: completed, invoiced (terminal happy)
 *   - danger:  cancelled, failed (terminal sad)
 * 'active' survives as a tone for surfaces that render MOTION (the stop
 * projection) or history entries from the old ladder.
 */

export type StatusTone = 'neutral' | 'info' | 'active' | 'success' | 'danger';

const TONE: Record<LoadStatus, StatusTone> = {
  requested: 'neutral',
  draft: 'neutral',
  confirmed: 'info',
  assigned: 'info',
  completed: 'success',
  invoiced: 'success',
  cancelled: 'danger',
  failed: 'danger',
};

const LABEL: Record<LoadStatus, string> = {
  requested: 'Requested',
  draft: 'Draft',
  confirmed: 'Confirmed',
  assigned: 'Assigned',
  completed: 'Completed',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

export function statusTone(status: LoadStatus): StatusTone {
  return TONE[status];
}

export function statusLabel(status: LoadStatus): string {
  return LABEL[status];
}

/**
 * Motion (the stop projection, S79) in card voice — collection/delivery,
 * never pickup/dropoff on glass. Total: null/undefined in, null out.
 */
const MOTION_LABEL: Record<NonNullable<LoadMotion>, string> = {
  waiting: 'Waiting to start',
  en_route_pickup: 'Heading to collection',
  at_pickup: 'At collection',
  loaded: 'Loaded',
  en_route_dropoff: 'In transit',
  at_dropoff: 'At delivery',
  delivered: 'Delivered',
};

export function motionLabel(motion: LoadMotion | undefined): string | null {
  return motion ? MOTION_LABEL[motion] : null;
}

/** Per-stop status chips on the job sheet — same tone palette as the badges. */
const STOP_LABEL: Record<LoadStopStatus, string> = {
  pending: 'Pending',
  en_route: 'En route',
  arrived: 'Arrived',
  completed: 'Done',
  skipped: 'Skipped',
  failed: 'Failed',
};

const STOP_TONE: Record<LoadStopStatus, StatusTone> = {
  pending: 'neutral',
  en_route: 'active',
  arrived: 'active',
  completed: 'success',
  skipped: 'active',
  failed: 'danger',
};

export function stopStatusLabel(status: LoadStopStatus): string {
  return STOP_LABEL[status];
}

export function stopStatusTone(status: LoadStopStatus): StatusTone {
  return STOP_TONE[status];
}

/**
 * History-tolerant edges (S79): the ledger's from/to are strings by design
 * — old-ladder values survive verbatim forever. Translate known tokens in
 * card voice (collection/delivery, never pickup/dropoff on glass); title-case
 * anything unrecognised. Never narrow these back to LoadStatus.
 */
const LOOSE_LABEL: Record<string, string> = {
  ...LABEL,
  quoted: 'Quoted',
  accepted: 'Accepted',
  en_route_pickup: 'To collection',
  at_pickup: 'At collection',
  loaded: 'Loaded',
  en_route_dropoff: 'To delivery',
  at_dropoff: 'At delivery',
  delivered: 'Delivered',
  pod_captured: 'Proof filed',
};

const LOOSE_TONE: Record<string, StatusTone> = {
  ...TONE,
  quoted: 'neutral',
  accepted: 'info',
  en_route_pickup: 'active',
  at_pickup: 'active',
  loaded: 'active',
  en_route_dropoff: 'active',
  at_dropoff: 'active',
  delivered: 'success',
  pod_captured: 'success',
};

export function historyLabel(s: string): string {
  return LOOSE_LABEL[s] ?? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function historyTone(s: string): StatusTone {
  return LOOSE_TONE[s] ?? 'neutral';
}

/**
 * Filter chips shown on the loads list. Order matters — left to right
 * follows the operational flow. (The queue board — Pipeline / Unassigned /
 * per-driver — is its own later step; these chips are the interim slice.)
 */
export const STATUS_FILTERS: Array<{
  key: LoadStatus | 'open' | null;
  label: string;
}> = [
  { key: null, label: 'All' },
  { key: 'open', label: 'Active' }, // synthetic — maps to a set of statuses
  { key: 'draft', label: 'Draft' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

/**
 * "Active" is a synthetic filter — committed work that isn't done:
 * confirmed (ready to place) + assigned (on a driver's queue, moving or
 * about to). Motion detail lives on the stops, not here.
 */
export const ACTIVE_STATUSES: LoadStatus[] = ['confirmed', 'assigned'];
